import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { Company } from '@macs-frontend/web-macs/shared';
import * as fns from 'date-fns/fp';
import { either as E, function as F, option as O } from 'fp-ts';
import { Subject } from 'rxjs';
import { CurrentCompanyService } from '../services/current-company.service';
import { defaultDateFormat } from '../utils/consts';
import { rx } from '../utils/rx';

@Injectable({
  providedIn: 'root',
})
export class AppDateAdapter extends NativeDateAdapter implements OnDestroy {
  private destroy$ = new Subject<void>();
  private currentCompany?: Company;
  private dateFormat = defaultDateFormat;
  private startWeek = 1;

  constructor(
    @Inject(MAT_DATE_LOCALE) matDateLocale: string,
    @Optional() currentCompanyService: CurrentCompanyService,
  ) {
    super(matDateLocale);
    if (currentCompanyService?.currentCompany$) {
      currentCompanyService.currentCompany$
        .pipe(rx.takeUntil(this.destroy$))
        .subscribe((company: Company) => {
          this.currentCompany = company;
          this.dateFormat = this.currentCompany?.dateFormat || defaultDateFormat;
          this.startWeek =
            this.currentCompany && this.currentCompany.startWeek
              ? this.currentCompany.startWeek
              : 1;
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  parse(value?: string | Date | null): Date | null {
    return F.pipe(
      value,
      O.fromNullable,
      O.fold(
        () => null,
        value =>
          F.pipe(
            value instanceof Date ? E.right(value) : E.left(value),
            E.fold(F.flow(fns.parse(new Date())(this.dateFormat)), F.identity),
          ),
      ),
    );
  }
  format(date: Date | (Date & { format: string }), displayFormat: string): string {
    if ('format' in date && date.format) {
      return F.pipe(date, fns.format(date.format));
    }

    if (displayFormat === 'input') {
      return F.pipe(date, fns.format(this.dateFormat));
    } else if (displayFormat === 'inputMonth') {
      return F.pipe(date, fns.format('yyyy/MM'));
    } else {
      return date.toDateString();
    }
  }

  getFirstDayOfWeek(): number {
    return this.startWeek !== 7 ? this.startWeek : 0;
  }
}

export const APP_DATE_FORMATS = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'inputMonth',
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};
